import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, useEffect, ChangeEvent } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/VerifyPinFlowConfig";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { FormError } from "@/models/continente-credentials/common/FormError"
import { EmailPinValidationRequest, PhoneNumberPinValidationRequest } from "@/models/shared/PinValidationRequest";

import { divideEmail } from "@/utils/EmailDivider";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import Divider from "@/components/divider";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";

import { cn } from "@/utils/utils";

import { pinValidationService } from "@/services/shared/PinValidationService";
import { genericService } from "@/services/GenericService";
import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";
import Input from "@/components/ui/Input";


const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface VerifyPinPageProps {
  flowConfig: FlowConfig;
}

type Form = (PhoneNumberPinValidationRequest | EmailPinValidationRequest) & FormError;

export default function VerifyPinPage({ flowConfig }: VerifyPinPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();

  const { handleResponse } = useApiResponseHandler();

  const [loading, setLoading] = useState(false);
  const [labelPINVisible, setLabelPINVisible] = useState<boolean>(false);
  const [isAdminBlocked, setAdminBlocked] = useState(false);

  const userData = location.state?.properties || {};
  const userIdentifier = userData.identifier || userData[flowConfig.identifierField] || "";
  const nextStep = location.state?.nextStep || "";
  const phoneFreshStartEligible = userData.phoneFreshStartEligible === true;

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleContactFormLinkClick = () => {
    history.push("/continente-credentials/faq" + ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION);
  };

  const handlePINChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setValue("pin", value);
    setLabelPINVisible(value.length > 0 ? true : false);
  };

  useEffect(() => {
    const pinValidator = (register: UseFormRegister<Form>) =>
      register("pin", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]{4,6}/,
          message: t("pin_validation.not_correct_pin_message"),
        },
      });
    pinValidator(register);
  }, [register, t]);

  const onSubmit: SubmitHandler<PhoneNumberPinValidationRequest | EmailPinValidationRequest> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(
      pinValidationService.validate(
        {
          pin: data.pin,
          ...(flowConfig.identifierField === 'email' ?
            { email: userIdentifier } :
            { phoneNumber: userIdentifier })
        },
        nextStep
      )
    );

    handleResponse(response, {
      setError,
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key !== "Enter" &&
      (!e.key.match(/[0-9\s]/) ||
        (e.target as HTMLInputElement).value.length > 6)
    ) {
      e.preventDefault();
    }
  };

  const recoverPINClick = async () => {
    setLoading(true);
    const { data: response, error } = await tryCatch(pinValidationService.recover(nextStep));

    handleResponse(response, {
      setError,
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  const freshStart = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: flowConfig.freshStartOrigin
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  return (
    <CredentialsForm
      id={flowConfig.form}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={cn(
          t(flowConfig.labels.headerSubtitle ?? ""),
          (flowConfig.identifierField == "email" ? divideEmail(userIdentifier) : userIdentifier))
        }
        showLoading={loading}
        previousStepBackXTimes={2}
      />
      <Input
        id="pin__input"
        labelText={t("pin_validation.pin_input")}
        isFilled={labelPINVisible}
        isError={!!(errors.pin || errors.blocked || errors.submitError)}
        errorMsg={
          errors.pin?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        errorMsgBlocked={errors.blocked ? errors.blocked.message : ""}
        onChange={handlePINChange}
        onKeyPress={handleKeyPress}
        type={"password"}
        inputMode="numeric"
        ariaLabel={t("accessibility.phonenumber.login.verify_pin.input")}
      />
      <div className="flex justify-center">
        <Button
          type="button"
          variant={"link"}
          size={"link"}
          onClick={recoverPINClick}>
          {t("pin_validation.forgot_message")}
        </Button>
      </div>
      <Button
        disabled={loading}>
        {t(flowConfig.labels.nextButton)}
      </Button>
      {flowConfig.showFreshStart && phoneFreshStartEligible && (
        <>
          <Divider title={t("common.or")} />
          <FreshStartButton onClick={freshStart} />
        </>
      )}
      <NeedHelpButton />
      <CredentialsFooter
        contextualInformationVisible={false}
        footerAppsWebsitesIconsVisible={false}
      />
    </CredentialsForm>
  );
}

export const createValidatePasswordPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <VerifyPinPage flowConfig={config} />;
};

export const PhonePinValidation = createValidatePasswordPage(AuthFlowType.PHONE_LOGIN);
export const EmailPinValidation = createValidatePasswordPage(AuthFlowType.EMAIL_LOGIN);
